import React, { useEffect, useState } from 'react';
import './ZipGate.scss';
import TextRender from '../TextRender/TextRender';
import { LinkButton, LinkButtonType } from '../LinkButton/LinkButton';
import {
  allowGeolocation,
  getZipcode,
  getZipUrl,
  setZipcode,
} from 'helpers/Zipgate';
import { checkURL, trackEvent } from 'helpers/Analytics';
import { IZipGateLabels } from 'interfaces/zipgate';
import AhmIcon from '../AhmIcon/AhmIcon';

export interface IZipgateProps {
  labels: IZipGateLabels;
  zipType?: string;
  pageHeading?: string;
  isLocator?: boolean;
  closeFunction?: () => void;
}

export default function ZipGate({
  labels,
  zipType,
  pageHeading,
  isLocator,
  closeFunction,
}: IZipgateProps): JSX.Element {
  const [zipcodeInput, setZipcodeInput] = useState('');
  const [zipStatus, setZipStatus] = useState(0);
  const [geolocationStatus, setGeolocationStatus] = useState(0);
  const isHonda = process.env.REACT_APP_THEME === 'honda';
  const zipgateType = isHonda ? 'zipgate-honda' : 'zipgate';
  const trackingObject = isHonda
    ? { Page: { page_name: '' } }
    : window.Acr.trackingObject || {};
  const dspToken = sessionStorage.getItem('dsp_token');
  const trackEventLocator = isHonda
    ? {
        label: 'location access:zip submit',
        category: {
          text: 'user location access modal',
          includePageName: false,
        },
        type: 'cta',
      }
    : isLocator
    ? {
        label: `location access:zip submit`,
        category: {
          text: 'user location access modal',
          includePageName: false,
        },
        type: 'cta',
        modelSeries: 'zdx',
        modelYear: '2024',
        bodyStyle: 'suvs',
        environment: checkURL(trackingObject.Page?.full_url || ''),
        locator: {
          Page: {
            ...trackingObject.Page,
            page_friendly_url: trackingObject.Page?.full_url || '',
          },
          User: {
            user_zipcode: getZipcode(),
            user_status: dspToken ? 'logged_in' : 'not_logged_in',
          },
          GlobalMetadata: {
            property_name: 'acura.com',
            version_dl: 'V1.0',
          },
        },
      }
    : {
        label: `location access:zip submit`,
        category: {
          text: 'user location access modal',
          includePageName: false,
        },
        type: 'cta',
      };

  useEffect(() => {
    setZipcodeInput('');
    setZipStatus(0);
    setGeolocationStatus(0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleZipgateClose = () => {
    if (closeFunction) {
      closeFunction();
    }
  };

  const handleInputChange = (event: any) => {
    const inputValue = event.target.value;
    var hondaElement = document.getElementsByClassName(
      'zipgate-honda__label-container'
    )[0] as HTMLElement;
    if (inputValue.length <= 5) {
      if (isHonda) {
        if (inputValue.length > 0) {
          hondaElement.style.visibility = 'visible';
        } else {
          hondaElement.style.visibility = 'hidden';
        }
      }
      setZipStatus(0);
      setZipcodeInput(inputValue);
    }
  };

  const validateZip = async () => {
    setZipStatus(1);
    const response: any = await getZipUrl(zipcodeInput);
    if (response && response.data && response.data[zipcodeInput]) {
      const zipData = response.data[zipcodeInput];
      const result =
        zipData.address &&
        zipData.address.countryRegion === 'United States' &&
        zipData.confidence === 'High';
      if (result) {
        trackEvent(
          zipType === 'estimatepayments'
            ? {
                label: `zip gate:zip search`,
                category: {
                  text: 'payment calculator zip gate',
                  includePageName: false,
                },
                type: 'selector',
                //missing current offer zipcode prop50
              }
            : trackEventLocator
        );
        setZipcode(zipcodeInput);
        handleZipgateClose();
      } else {
        setZipStatus(2);
      }
    } else {
      setZipStatus(2);
    }
  };

  const getGeolocation = async () => {
    setGeolocationStatus(1);
    const response: any = await allowGeolocation();
    if (response && response.data.GeoIpData) {
      const zipCode = response.data.GeoIpData.postalCode || '';
      const isUS = response.data.GeoIpData?.country === 'US';
      if (response.data.ResolveState === 1 && zipCode && isUS) {
        if (isLocator) {
          trackEvent({
            label: `location access:${(
              labels.geolocationCta || ''
            ).toLowerCase()}`,
            category: {
              text: 'user location access modal',
              includePageName: false,
            },
            type: 'cta',
            modelSeries: 'zdx',
            modelYear: '2024',
            bodyStyle: 'suvs',
            environment: checkURL(trackingObject.Page?.full_url || ''),
            locator: {
              Page: {
                ...trackingObject.Page,
                page_friendly_url: trackingObject.Page?.full_url || '',
              },
              User: {
                user_zipcode: getZipcode(),
                user_status: dspToken ? 'logged_in' : 'not_logged_in',
              },
              GlobalMetadata: {
                property_name: 'acura.com',
                version_dl: 'V1.0',
              },
            },
          });
        } else {
          trackEvent({
            label: `location access:${(
              labels.geolocationCta || ''
            ).toLowerCase()}`,
            category: {
              text: 'user location access modal',
              includePageName: false,
            },
            type: 'cta',
          });
        }
        setZipcode(zipCode);
        handleZipgateClose();
      } else {
        setGeolocationStatus(2);
      }
    } else {
      setGeolocationStatus(2);
    }
  };

  return (
    <div className="zipgate">
      {/* Used to display a h1 any time a zipgate is open */}
      {pageHeading && <h1 className="zipgate__hidden-header">{pageHeading}</h1>}
      <div className="zipgate__data-container">
        <h2 className={`acr-title-3 t-location ${zipgateType}__location-text`}>
          <TextRender text={labels.zipgateTitle || ''} />
        </h2>
        <p
          className={`acr-paragraph-5 t-disclaimer_text zipgate__disclaimer-text`}
        >
          <TextRender text={labels.zipgateDescription || ''} />
        </p>
        <div className="zipgate__action-container">
          <LinkButton
            variant={isHonda ? LinkButtonType.evAutosRound : LinkButtonType.red}
            classNames={{
              container: `${zipgateType}__use-location-button new t-use-location-btn`,
            }}
            ariaLabelledby={'geolocation-error'}
            onClick={() => getGeolocation()}
          >
            <TextRender text={labels.geolocationCta || ''} />
          </LinkButton>
          <span className={`acr-paragraph-6 zipgate__separator-text`}>OR</span>
          <div className={`${zipgateType}__form-container`}>
            <div className={`${zipgateType}__input-container`}>
              {isHonda && (
                <div className="zipgate-honda__label-container">
                  <p className="zipgate-honda__label ahm-legacy-label3">
                    ZIP Code
                  </p>
                </div>
              )}
              <input
                className={`acr-paragraph-6 ${zipgateType}__input t-zip-input`}
                aria-label="zipcode"
                aria-labelledby="zip-error"
                name="zipcode"
                placeholder={labels.zipPlaceholder || ''}
                maxLength={5}
                type="number"
                value={zipcodeInput}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && zipcodeInput.length === 5) {
                    validateZip();
                  }
                }}
                onChange={handleInputChange}
                disabled={zipStatus === 1}
                data-automation="zipcode-field"
              />
              <AhmIcon classes="zipgate__input-icon" iconName="location" />
            </div>
            {zipStatus === 1 ? (
              <div className={`zipgate__search-button active`}>
                <div className={`${zipgateType}__button-spinner`} />
              </div>
            ) : (
              <LinkButton
                variant={
                  isHonda ? LinkButtonType.simpleEVAutos : LinkButtonType.simple
                }
                dataAutomation="zipcode-input-button"
                ariaLabel="Submit ZIP Code"
                classNames={{
                  container: `${zipgateType}__search-button new ${
                    zipcodeInput.length === 5 ? 'active' : ''
                  } t-zip-btn`,
                }}
                onClick={() => validateZip()}
                disabled={zipcodeInput.length !== 5}
              >
                {isHonda ? (
                  <AhmIcon iconName="search" />
                ) : (
                  <AhmIcon iconName="searchAlternative" />
                )}
              </LinkButton>
            )}
            {zipStatus === 2 && (
              <p
                role="alert"
                id="zip-error"
                className={`acr-paragraph-9 zipgate__zip-error-text t-zip-error`}
              >
                <TextRender text={labels.zipError || ''} />
              </p>
            )}
          </div>
        </div>
        {geolocationStatus === 2 && (
          <p
            role="alert"
            id="geolocation-error"
            className={`acr-paragraph-9 zipgate__geo-error-text t-geo-error`}
          >
            <TextRender text={labels.geolocationError || ''} />
          </p>
        )}
      </div>
      {geolocationStatus === 1 && (
        <div className="zipgate__spinner-container">
          <div className={`${zipgateType}__spinner`} />
        </div>
      )}
    </div>
  );
}
